import React, { useEffect, useState } from "react";
import Joyride from "react-joyride";

import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  Skeleton,
  useMediaQuery,
  Grid, // Import Grid component
} from "@mui/material";
import Swal from "sweetalert2";
import { customerservice, reportsManagementUrl } from "../../../../../config";
import { generateReport } from "../../../../../data/DynamicTable/DynamicFunctions";
import {
  fetchCustomerStatementJsonDynamic,
  fetchJsonDynamic,
} from "../../../../../data/DynamicServiceNew/jsondynamicservice";
import { tokens } from "../../../../../theme";

const GroupStatementDetails = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [isFetching, setIsFetching] = useState(true);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const customerId = localStorage.getItem("GroupId");
const [runTour, setRunTour] = useState(true); // Automatically starts the tour
const [steps] = useState([
  {
    target: ".report-details",
    content: "This section displays summary details about the group report.",
  },
  {
    target: ".start-date",
    content: "Select the start date for the group statement.",
  },
  {
    target: ".end-date",
    content: "Now choose the end date.",
  },
  {
    target: ".generate-report",
    content: "Click here to generate the report for the selected period.",
  },
]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const variables = { groupid: parseInt(customerId) };
        const response = await fetchCustomerStatementJsonDynamic(
          "groupLoanAndSavingsById",
          "groupid",
          variables,
          customerservice.uri
        );
        setData(JSON.parse(response.results));
      } catch (error) {
        console.error("Error fetching customer data:", error);
      } finally {
        setIsFetching(false);
      }
    };

    fetchData();
  }, [customerId]);

  const handleGenerateReport = async () => {
    const reportTypeId = 43;
    const reportType = "Group Statement";

    try {
      setIsLoading(true);
      const mutationData = {
        ReportTypeId: reportTypeId,
        ReportType: reportType,
        CustomerId: parseInt(customerId),
        StartDate: startDate,
        EndDate: endDate,
      };

      const newTab = window.open("", "_blank");
      newTab.document.write("<p>Loading...</p>");

      const response = await generateReport(
        mutationData,
        reportsManagementUrl.generate_report_uri,
        setIsLoading
      );

      if (response.ok) {
        const htmlContent = await response.text();
        newTab.document.open();
        newTab.document.write(htmlContent);
        newTab.document.close();
      } else {
        newTab.close();
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: "Failed to generate the report. Please try again later.",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while generating the report.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fields = [
    {
      label: "LoanID",
      value: `${data?.loanid || "N/A"}`,
    },
    { label: "First Name", value: data?.first_name || "N/A" },
    { label: "Group Code", value: data?.group_code || "N/A" },
    {
      label: "Date Created",
      value: data?.date_created
        ? new Date(data?.date_created).toLocaleDateString()
        : "N/A",
    },
    { label: "Loan Amount", value: `KSH ${data?.active_loan || 0.0}` },
    { label: "OLB", value: `KSH ${data?.olb || 0.0}` },
    { label: "Arrears", value: `KSH ${data?.loan_arrears || 0.0}` },
    { label: "Days Overdue", value: data?.days_overdue || 0 },
    {
      label: "BBF Account Balance",
      value: `KSH ${data?.bbf_account_balance || 0.0}`,
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        padding: { xs: 1, sm: 2 },
        maxWidth: "1200px",
        margin: "0 auto",
      }}
    >
      <Joyride
  steps={steps}
  run={runTour}
  continuous
  showSkipButton
  showProgress
  styles={{
    options: {
      primaryColor: "#1976d2",
      zIndex: 10000,
    },
  }}
/>
<Button
  onClick={() => setRunTour(true)}
  variant="outlined"
  sx={{ alignSelf: "flex-end" }}
>
  Start Tour
</Button>

      <Box className="report-details" sx={{ marginBottom: 3 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            textAlign: { xs: "center", sm: "left" },
            mb: 3,
            fontSize: "1rem",
          }}
        >
          Group Details
        </Typography>
        {isSmallScreen ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              background: colors.primary[200],
              borderRadius: 2,
              padding: 2,
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
            }}
          >
            {fields.map((field, index) => (
              <Grid
                container
                key={index}
                sx={{
                  borderBottom:
                    index !== fields.length - 1 ? "1px solid #ccc" : "none", // Add border except for the last item
                  py: 1, // Add padding to separate rows
                }}
              >
                <Grid item xs={6}>
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    {field.label}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ textAlign: "right" }}>
                    {field.value}
                  </Typography>
                </Grid>
              </Grid>
            ))}
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              background: `${colors.primary[200]} !important`,
              borderRadius: 2,
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
              overflowX: "auto",
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  {fields.map((field, index) => (
                    <TableCell
                      key={index}
                      sx={{
                        background: colors.primary[400],
                        fontSize: "0.65rem",
                        fontWeight: "bold",
                      }}
                    >
                      {field.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {isFetching ? (
                  <TableRow>
                    {fields.map((_, index) => (
                      <TableCell key={index}>
                        <Skeleton variant="text" height={40} />
                      </TableCell>
                    ))}
                  </TableRow>
                ) : (
                  <TableRow>
                    {fields.map((field, index) => (
                      <TableCell key={index} sx={{ fontSize: "0.73rem" }}>
                        {field.value}
                      </TableCell>
                    ))}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Box>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            textAlign: { xs: "center", sm: "left" },
            mb: 2,
            fontSize: "1rem",
          }}
        >
          Generate Group Statement
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            background: colors.primary[200],
            borderRadius: 2,
            padding: { xs: 1.5, sm: 2 },
            maxWidth: "300px",
          }}
        >
          <TextField
            className="start-date"
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            size="small"
            sx={{ background: colors.primary[400], borderRadius: 1 }}
          />
          <TextField
            className="end-date"
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            size="small"
            sx={{ background: colors.primary[400], borderRadius: 1 }}
          />
          <Button
            className="generate-report"
            variant="contained"
            color="primary"
            onClick={handleGenerateReport}
            disabled={isLoading}
            fullWidth
            size="small"
            sx={{
              background: colors.blueAccent[500],
              "&:hover": { background: colors.blueAccent[600] },
            }}
          >
            {isLoading ? <CircularProgress size={20} /> : "Generate Report"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default GroupStatementDetails;
